import { useEffect, useState } from 'react';

/**
 * 動画の読み込み状態を管理するカスタムフック（オフスクリーンで検証）
 * @param {string} videoUrl - 対象の動画URL
 * @param {object} options  - { timeout?: number, preloadNextUrl?: string }
 * @returns {{
 *   isLoading: boolean,
 *   isLoaded: boolean,
 *   hasError: boolean,
 *   canRetry: boolean,
 *   isPreloading: boolean,
 *   retry: () => void
 * }}
 */
export function useVideoLoader(videoUrl, options = {}) {
  const { preloadNextUrl, timeout = 8000 } = options;

  const [isLoading, setIsLoading] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [canRetry, setCanRetry] = useState(false);
  const [isPreloading, setIsPreloading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // メイン動画の読み込み検知（画面外の <video> で判定）
  useEffect(() => {
    let cancelled = false;
    if (!videoUrl) {
      setIsLoading(false);
      setIsLoaded(false);
      setHasError(true);
      setCanRetry(true);
      return;
    }

    setIsLoading(true);
    setIsLoaded(false);
    setHasError(false);
    setCanRetry(false);

    const v = document.createElement('video');
    v.preload = 'metadata';
    v.playsInline = true;
    v.muted = true; // iOSの挙動安定
    v.src = videoUrl;

    const timer = setTimeout(() => {
      if (cancelled) return;
      setIsLoading(false);
      setIsLoaded(false);
      setHasError(true);
      setCanRetry(true);
    }, timeout);

    const onReady = () => {
      if (cancelled) return;
      clearTimeout(timer);
      setIsLoading(false);
      setIsLoaded(true);
      setHasError(false);
      setCanRetry(false);
    };

    const onError = () => {
      if (cancelled) return;
      clearTimeout(timer);
      setIsLoading(false);
      setIsLoaded(false);
      setHasError(true);
      setCanRetry(true);
    };

    v.addEventListener('loadeddata', onReady, { once: true });
    v.addEventListener('error', onError, { once: true });
    // 明示的に読み込ませる
    try { v.load(); } catch {}

    return () => {
      cancelled = true;
      clearTimeout(timer);
      v.removeEventListener('loadeddata', onReady);
      v.removeEventListener('error', onError);
      // 取り消し（モバイルでのネットワーク節約）
      try { v.src = ''; v.removeAttribute('src'); v.load(); } catch {}
    };
  }, [videoUrl, timeout, reloadKey]);

  // 次の動画を軽くプリロード（存在すれば）
  useEffect(() => {
    if (!preloadNextUrl) {
      setIsPreloading(false);
      return;
    }
    let cancelled = false;

    // rel=prefetch はヒントとして追加
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.as = 'video';
    link.href = preloadNextUrl;
    document.head.appendChild(link);

    // 実際にメタデータまで取得しておく（成功/失敗はUIのスピナー用）
    setIsPreloading(true);
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.src = preloadNextUrl;

    const done = () => { if (!cancelled) setIsPreloading(false); };
    v.addEventListener('loadeddata', done, { once: true });
    v.addEventListener('error', done, { once: true });
    try { v.load(); } catch {}

    return () => {
      cancelled = true;
      document.head.removeChild(link);
      v.removeEventListener('loadeddata', done);
      v.removeEventListener('error', done);
      try { v.src = ''; v.removeAttribute('src'); v.load(); } catch {}
    };
  }, [preloadNextUrl]);

  const retry = () => {
    setReloadKey((n) => n + 1);
  };

  return {
    isLoading,
    isLoaded,
    hasError,
    canRetry,
    isPreloading,
    retry,
  };
}
